import { logger } from "../utils/logger";
import { Sparkles, ArrowRight, Shield, Star, Award, Handshake, Target, Check, CheckCircle2, Users, Globe2, Briefcase, TrendingUp, Zap, Clock, Rocket, Mail, CheckCircle, X, ClipboardCheck, Phone, FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { toast } from 'sonner';
import { apiFetch, safeJsonParse } from '../utils/api';
import { Helmet } from 'react-helmet-async';

// Lazy — only needed when user opens the planner
const VideoPlannerModal = lazy(() =>
  import('../components/VideoPlannerModal').then(m => ({ default: m.VideoPlannerModal }))
);

// Icon map - matches options available in ContactHeroEditor
const iconMap: { [key: string]: any } = {
  Sparkles, Star, Award, Target, Check, CheckCircle2, Users, Globe2, Shield,
  Briefcase, Handshake, TrendingUp, Zap, Clock, Rocket, Mail,
};

// ============================================
// SKELETON
// ============================================

const ContactPageSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
    {/* Hero skeleton */}
    <section className="relative flex items-center px-4 sm:px-6 lg:px-8 py-12 pt-20">
      <div className="max-w-4xl mx-auto w-full text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-44 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="space-y-2 max-w-2xl mx-auto">
          <div className="h-9 bg-gray-200 rounded w-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="h-9 bg-gray-200 rounded w-3/4 mx-auto relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="flex justify-center gap-2 flex-wrap">
          {[96, 112, 88, 100].map((w, i) => (
            <div key={i} className="h-8 bg-gray-200 rounded-full relative overflow-hidden" style={{ width: w }}>
              <div className="absolute inset-0 shimmer" />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Form skeleton */}
    <section className="relative py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white/80 rounded-2xl border border-gray-200/50 shadow-lg p-5 sm:p-7 space-y-4">
          <div className="text-center space-y-2 mb-5">
            <div className="h-7 bg-gray-200 rounded w-64 mx-auto relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-4 bg-gray-200 rounded w-80 mx-auto relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          {/* Two-col row x2 */}
          {[0, 1].map(row => (
            <div key={row} className="grid sm:grid-cols-2 gap-3">
              {[0, 1].map(col => (
                <div key={col} className="space-y-1.5">
                  <div className="h-3 w-20 bg-gray-200 rounded relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                  <div className="h-10 bg-gray-200 rounded-lg relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                </div>
              ))}
            </div>
          ))}
          {/* Select */}
          <div className="space-y-1.5">
            <div className="h-3 w-24 bg-gray-200 rounded relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-10 bg-gray-200 rounded-lg relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          {/* Textarea */}
          <div className="space-y-1.5">
            <div className="h-3 w-28 bg-gray-200 rounded relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-20 bg-gray-200 rounded-lg relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          {/* Button */}
          <div className="h-12 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
      </div>
    </section>
  </div>
);

// ============================================
// VIDEO PLANNER CTA
// ============================================

interface VideoPlannerCTAProps {
  onOpen: () => void;
}

function VideoPlannerCTA({ onOpen }: VideoPlannerCTAProps) {
  const benefits = [
    'Map your demo scenarios',
    'Define your audience',
    'Walk in with clarity',
  ];

  return (
    <section className="relative py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">

        {/* Outer card — matches ContactPage card style exactly */}
        <div className="relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg">

          {/* Left accent strip — orange gradient matching page theme */}
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#E87D3E] to-[#E8B84D]" />



          <div className="relative p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">

              {/* Icon block */}
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E87D3E]/10 to-[#E8B84D]/10 border border-[#E87D3E]/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#E87D3E]" />
                </div>
              </div>

              {/* Text content */}
              <div className="flex-1 min-w-0">
                {/* Badge */}
                <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#E87D3E]/10 to-[#E8B84D]/10 border border-[#E87D3E]/20 px-2.5 py-1 rounded-full mb-2">
                  <Download className="w-3 h-3 text-[#E87D3E]" />
                  <span className="text-[10px] font-bold text-[#E87D3E] tracking-wider uppercase">
                    Free Resource
                  </span>
                </div>

                <h3 className="text-base font-bold text-[#2F2E5C] mb-1 leading-snug">
                  Not sure where to begin?{' '}
                  <span className="bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                    Download our Video Planning Template.
                  </span>
                </h3>

                <p className="text-sm text-gray-500 mb-3 leading-relaxed">
                  The exact framework we use to map 3D demo bundles —
                  fill it in before our call and hit the ground running.
                </p>

                {/* Benefit pills */}
                <div className="flex flex-wrap gap-2">
                  {benefits.map((b) => (
                    <span
                      key={b}
                      className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-50 border border-gray-200/80 px-2.5 py-1 rounded-full"
                    >
                      <CheckCircle2 className="w-3 h-3 text-[#E87D3E] flex-shrink-0" />
                      {b}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA button */}
              <div className="flex-shrink-0 sm:self-center">
                <button
                  onClick={onOpen}
                  className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] hover:from-[#E8B84D] hover:to-[#E87D3E] text-white text-sm font-bold px-5 py-3 rounded-full shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap"
                >
                  Get the template
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </button>
              </div>

            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

// ============================================
// COMPONENT
// ============================================

export function ContactPage() {
  const [heroData, setHeroData] = useState<any>(null);
  const [formSectionData, setFormSectionData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [contactSettings, setContactSettings] = useState<any>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    videoType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showVideoPlannerModal, setShowVideoPlannerModal] = useState(false);

  useEffect(() => {
    fetchCMSData();

    const handleHeroChanged = () => fetchCMSData();
    const handleFormSectionChanged = () => fetchCMSData();

    window.addEventListener('contactHeroChanged', handleHeroChanged);
    window.addEventListener('contactFormSectionChanged', handleFormSectionChanged);

    return () => {
      window.removeEventListener('contactHeroChanged', handleHeroChanged);
      window.removeEventListener('contactFormSectionChanged', handleFormSectionChanged);
    };
  }, []);

  const fetchCMSData = async () => {
    try {
      const [heroResponse, formResponse] = await Promise.all([
        apiFetch('/contact/hero', { method: 'GET' }),
        apiFetch('/contact/form-section', { method: 'GET' })
      ]);

      if (heroResponse.ok) {
        const heroResult = await safeJsonParse(heroResponse);
        if (heroResult.success) setHeroData(heroResult.data);
      }

      const settingsResponse = await apiFetch('/contact/settings', { method: 'GET' });
      if (settingsResponse.ok) {
        const settingsResult = await safeJsonParse(settingsResponse);
        if (settingsResult.success) setContactSettings(settingsResult.data);
      }

      if (formResponse.ok) {
        const formResult = await safeJsonParse(formResponse);
        if (formResult.success) setFormSectionData(formResult.data);
      }
    } catch (error) {
      logger.error('❌ [CONTACT] Error fetching CMS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await apiFetch('/forms/submit', {
        method: 'POST',
        body: JSON.stringify({
          form_type: 'contact',
          form_name: 'Contact Page',
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          videoType: formData.videoType,
          message: formData.message,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('❌ [CONTACT FORM] Server error response:', errorText);
        toast.error('Failed to send message. Please try again or email us directly at hello@creabiz.co', {
          duration: 6000,
          position: 'top-center'
        });
        setIsSubmitting(false);
        return;
      }

      const result = await safeJsonParse(response);

      if (!result.success) {
        logger.error('❌ [CONTACT FORM] Server returned error:', result);
        toast.error('Failed to send message. Please try again or email us directly at hello@creabiz.co', {
          duration: 6000,
          position: 'top-center'
        });
        setIsSubmitting(false);
        return;
      }

      setShowSuccessModal(true);
      setFormData({ name: '', email: '', company: '', phone: '', videoType: '', message: '' });

    } catch (error: any) {
      logger.error('❌ [CONTACT FORM] Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again or email us directly at hello@creabiz.co', {
        duration: 7000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show skeleton while CMS data loads
  if (loading) {
    return <ContactPageSkeleton />;
  }

  return (
    <>
      <Helmet>
        <title>{contactSettings?.seo?.metaTitle || 'Contact Us | Creabiz Studio'}</title>
        <meta name="description" content={contactSettings?.seo?.metaDescription || 'Get in touch with Creabiz Studio. Receive a detailed animation proposal within 24 hours. No commitment required.'} />
        <meta property="og:title" content={contactSettings?.seo?.ogTitle || contactSettings?.seo?.metaTitle || 'Contact Us | Creabiz Studio'} />
        <meta property="og:description" content={contactSettings?.seo?.ogDescription || contactSettings?.seo?.metaDescription || 'Get in touch with Creabiz Studio. Receive a detailed animation proposal within 24 hours.'} />
        {contactSettings?.seo?.ogImage && <meta property="og:image" content={contactSettings.seo.ogImage} />}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={contactSettings?.seo?.ogTitle || contactSettings?.seo?.metaTitle || 'Contact Us | Creabiz Studio'} />
        <meta property="twitter:description" content={contactSettings?.seo?.ogDescription || contactSettings?.seo?.metaDescription || 'Get in touch with Creabiz Studio.'} />
      </Helmet>

      {/* Seamless Gradient Background for Entire Page */}
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">

        {/* Hero Section */}
        <section className="relative flex items-center px-4 sm:px-6 lg:px-8 py-12 pt-20">
          <div className="max-w-4xl mx-auto w-full relative z-10 text-center">

            {/* Premium Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-gray-200 shadow-sm">
              {iconMap[heroData?.badge?.icon] ? (
                React.createElement(iconMap[heroData.badge.icon], { className: "w-4 h-4 text-[#E87D3E]" })
              ) : (
                <Sparkles className="w-4 h-4 text-[#E87D3E]" />
              )}
              <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                {heroData?.badge?.text || "Let's Create Together"}
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 leading-tight text-[#2F2E5C]">
              {heroData?.title || 'Get Your Animation Proposal in'}{' '}
              <span className="bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                {heroData?.highlightedTitle || '24 Hours'}
              </span>
            </h1>

            {/* Description */}
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto mb-6 leading-relaxed">
              {heroData?.description || 'Tell us about your project and receive a detailed proposal with transparent pricing. No commitment required.'}
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
              {(heroData?.trustBadges || [
                { icon: '⭐', text: '5.0 Rating', visible: true, order: 0 },
                { icon: '⏰', text: '24hr Response', visible: true, order: 1 },
                { icon: '✓', text: 'Free Consult', visible: true, order: 2 },
                { icon: '🎬', text: '650+ Projects', visible: true, order: 3 }
              ]).filter((b: any) => b.visible).map((badge: any, i: number, arr: any[]) => (
                <React.Fragment key={i}>
                  <div className="inline-flex items-center gap-1.5 bg-white/60 backdrop-blur-sm px-3 py-1.5 rounded-full border border-gray-200/50">
                    <span>{badge.icon}</span>
                    <span className="font-medium text-gray-700">{badge.text}</span>
                  </div>
                  {i < arr.length - 1 && <span className="text-gray-300">|</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* Video Planner CTA — between Hero and Contact Form */}
        <VideoPlannerCTA onOpen={() => setShowVideoPlannerModal(true)} />

        {/* Contact Form */}
        <section className="relative py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg p-5 sm:p-7">

              {/* Form Header */}
              <div className="text-center mb-5">
                <h2 className="text-xl sm:text-2xl font-bold text-[#2F2E5C] mb-2">
                  {formSectionData?.formTitle || 'Tell Us About Your Project'}
                </h2>
                <p className="text-sm text-gray-600">
                  {formSectionData?.formSubtitle || "Fill out the form and we'll get back within 24 hours"}
                </p>
              </div>

              <form className="space-y-3.5" onSubmit={handleSubmit}>
                {/* Name + Email */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Full Name <span className="text-[#E87D3E]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:border-[#E87D3E] focus:ring-2 focus:ring-[#E87D3E]/20 focus:outline-none transition-all bg-white text-gray-900 placeholder:text-gray-400"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Email Address <span className="text-[#E87D3E]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:border-[#E87D3E] focus:ring-2 focus:ring-[#E87D3E]/20 focus:outline-none transition-all bg-white text-gray-900 placeholder:text-gray-400"
                      placeholder="john@company.com"
                      required
                    />
                  </div>
                </div>

                {/* Company + Phone */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Company Name <span className="text-[#E87D3E]">*</span>
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:border-[#E87D3E] focus:ring-2 focus:ring-[#E87D3E]/20 focus:outline-none transition-all bg-white text-gray-900 placeholder:text-gray-400"
                      placeholder="Your Company Inc."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Phone <span className="text-gray-400 text-[10px] font-normal">(Optional)</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:border-[#E87D3E] focus:ring-2 focus:ring-[#E87D3E]/20 focus:outline-none transition-all bg-white text-gray-900 placeholder:text-gray-400"
                      placeholder="+1 (234) 567-890"
                    />
                  </div>
                </div>

                {/* Video Type */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Type of Video <span className="text-[#E87D3E]">*</span>
                  </label>
                  <select
                    name="videoType"
                    value={formData.videoType}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:border-[#E87D3E] focus:ring-2 focus:ring-[#E87D3E]/20 focus:outline-none transition-all bg-white cursor-pointer text-gray-900"
                    required
                  >
                    <option value="">Select type</option>
                    <option value="2d-explainer">2D Explainer</option>
                    <option value="3d-product-demo">3D Product Demo</option>
                    <option value="3d-technical">3D Technical</option>
                    <option value="process-visualization">Process Visual</option>
                    <option value="training">Training</option>
                    <option value="marketing">Marketing</option>
                    <option value="not-sure">Not Sure</option>
                  </select>
                </div>

                {/* Project Details */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Project Details <span className="text-[#E87D3E]">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:border-[#E87D3E] focus:ring-2 focus:ring-[#E87D3E]/20 focus:outline-none transition-all resize-none bg-white text-gray-900 placeholder:text-gray-400"
                    placeholder="Tell us about your project goals, timeline, and any specific requirements..."
                    required
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] hover:from-[#E8B84D] hover:to-[#E87D3E] text-white rounded-full h-12 text-sm gap-2 shadow-lg hover:shadow-xl transition-all font-bold group disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Submit Your Request'}
                  {!isSubmitting && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                </Button>

                {/* Privacy Note */}
                <p className="text-[10px] text-gray-500 text-center flex items-center justify-center gap-1 pt-1">
                  <Shield className="w-2.5 h-2.5" />
                  Your data is protected. We respect your privacy.
                </p>
              </form>
            </div>
          </div>
        </section>

        {/* What Happens Next */}
        <section className="relative py-10 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-sm p-6 sm:p-8">
              <h3 className="text-lg font-bold text-[#2F2E5C] mb-6 text-center flex items-center justify-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#E87D3E] rounded-full" />
                {formSectionData?.whatHappensNextTitle || 'What Happens Next'}
              </h3>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {(formSectionData?.steps || [
                  { num: '1', text: 'We review your request within 24 hours', icon: '📋', visible: true, order: 0 },
                  { num: '2', text: 'Schedule a discovery call', icon: '📞', visible: true, order: 1 },
                  { num: '3', text: 'Receive detailed proposal', icon: '📄', visible: true, order: 2 },
                  { num: '4', text: 'Begin production', icon: '🚀', visible: true, order: 3 }
                ]).filter((s: any) => s.visible).map((step: any, i: number) => (
                  <div key={i} className="relative text-center">
                    <div className="text-3xl mb-2">{step.icon}</div>
                    <div className="inline-flex items-center justify-center w-6 h-6 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-full text-white text-xs font-bold mb-2">
                      {step.num}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.text}</p>
                    {i < 3 && (
                      <div className="hidden lg:block absolute top-8 -right-2.5 w-5 h-0.5 bg-gradient-to-r from-[#E87D3E]/30 to-transparent" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-in zoom-in duration-300">
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#2F2E5C] mb-3">Message Sent Successfully!</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Thank you for reaching out! We've received your message and will get back to you within <strong className="text-[#E87D3E]">24 hours</strong>.
              </p>

              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-5 mb-6">
                <h4 className="text-sm font-bold text-[#2F2E5C] mb-3 flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4 text-[#E87D3E]" />
                  What Happens Next
                </h4>
                <div className="space-y-2.5 text-left">
                  {['We review your request', 'Schedule a discovery call', 'Receive your detailed proposal'].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] flex items-center justify-center text-white text-xs font-bold">
                        {i + 1}
                      </div>
                      <p className="text-sm text-gray-600">{text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] hover:from-[#E8B84D] hover:to-[#E87D3E] text-white rounded-full h-12 text-sm font-bold shadow-lg hover:shadow-xl transition-all"
              >
                Got it, thanks!
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Video Planner Modal — same instance used in Header */}
      <Suspense fallback={null}>
        <VideoPlannerModal
          isOpen={showVideoPlannerModal}
          onClose={() => setShowVideoPlannerModal(false)}
        />
      </Suspense>
    </>
  );
}